// formulario-externo.component.ts
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ProspectoService } from 'app/services/module-recursos-humanos/prospectos.sevice';
import { UbicacionService } from 'app/services/module-perfiles-usuario/ubicacion.service';
import { cedulaEcuadorValidator } from 'app/utils/validate-ecuatorian-id_card.util';
import Swal from 'sweetalert2';
import { PrivacyPolicyService } from 'app/services/module-config/privacy-policies/privacy-policie.service';

export function adultValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const birthDate = new Date(control.value);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age < 18 ? { underage: true } : null;
}

interface TokenResult {
    employment_application_id: number;
    role_required: {
        id: number;
        nombre: string;
        external_id: string;
    };
    privacy_policy?: any | null;
    ok: boolean;
}

@Component({
    standalone: false,
    selector: 'app-public-prospecto-form',
    templateUrl: './public-prospecto-form.component.html',
    styleUrls: ['./public-prospecto-form.component.css'],
})
export class PublicProspectoFormComponent implements OnInit, OnDestroy {
    form!: FormGroup;
    token = '';
    loading = true;
    valido = false;
    mensajeError = '';
    cvNombre = '';
    tokenResult: TokenResult | null = null

    enviado = false;
    enviando = false;
    enviado_again = false;
    ubicaciones: any;

    provincias: any[] = [];
    cantones: any[] = [];
    parroquias: string[] = [];
    esEcuador = true;

    selectedProvincia: string = '';
    selectedCantones: any[] = []
    selectedParroquias: any[] = []

    maxBirthDate!: string;
    mensajeExito =
        'Usted ha completado el formulario, cualquier novedad se le informará al correo electrónico o télefono de contacto.';
    privacyPolicy: any | null = null;

    privacyAccepted = false;
    privacyContactAccepted = false;
    privacyTruthAccepted = false;

    privacyModalOpen = false;
    privacyPdfLoading = false;
    privacyPdfSafeUrl: SafeResourceUrl | null = null;
    private privacyPdfObjectUrl: string | null = null;
    constructor(
        private route: ActivatedRoute,
        private prospectoService: ProspectoService,
        private ubicacionService: UbicacionService,
        private policyService: PrivacyPolicyService,
        private fb: FormBuilder,
        private sanitizer: DomSanitizer,
    ) { }

    ngOnInit(): void {
        this.token = this.route.snapshot.paramMap.get('token') || '';

        this.prospectoService.validarToken(this.token).subscribe({
            next: (res) => {
                if (res.ok) {
                    this.tokenResult = res;
                    this.privacyPolicy = res.privacy_policy || null;

                    this.valido = true;
                    this.obtenerUbicaciones();
                    this.crearFormulario();
                    this.applyPrivacyGate();
                } else {
                    this.mensajeError = res.error || 'Token invalidado';
                }

                this.loading = false;
            },
            error: (err) => {
                this.mensajeError =
                    err.error?.error || 'No se pudo validar el enlace';
                this.loading = false;
            },
        });

        this.setMaxBirthDate();
    }
    ngOnDestroy(): void {
        this.revokePrivacyPdfObjectUrl();
        document.body.classList.remove('sp-policy-modal-open');
    }
    crearFormulario() {
        this.form = this.fb.group({
            employment_application_id: [this.tokenResult?.employment_application_id],

            privacy_policy_id: [this.privacyPolicy?.external_id || null],
            privacy_policy_external_id: [this.privacyPolicy?.external_id || null],
            data_treatment_accepted: [false],
            contact_authorization_accepted: [false],
            truth_declaration_accepted: [false],

            name: [null, Validators.required],
            father_surname: [null, Validators.required],
            mother_surname: [null, Validators.required],
            birth_date: [null, Validators.required],
            age: [{ value: null, disabled: false }, [Validators.required, adultValidator]],
            role_required_id: [this.tokenResult?.role_required.id, Validators.required],
            vocation: [null, Validators.required],
            work_experience: [null, Validators.required],
            cv: [null, Validators.required],
            id_card: [
                null,
                [
                    Validators.required,
                    Validators.pattern(/^[0-9]+$/),
                    Validators.minLength(10),
                    Validators.maxLength(10),
                    cedulaEcuadorValidator,
                ],
            ],
            civil_status: [null, [Validators.required]],
            email: [null, [Validators.required, Validators.email]],
            phone: [null, Validators.required],
            city: [null, [Validators.required]],
            country: [null, [Validators.required]],
            province: [null, [Validators.required]],
            canton: [null, [Validators.required]],
            village: [null, [Validators.required]],
            hood: [null],
            street: [null],
            blood_type: [null],
            formId: [this.token],
        });

        this.calcularEdadAutomaticamente();
    }

    enviar() {
        if (!this.canUseRecruitmentForm) {
            Swal.fire({
                icon: 'warning',
                title: 'Autorización requerida',
                text: 'Debe aceptar el tratamiento de datos personales para continuar con la postulación.',
                confirmButtonColor: '#e10600',
            });

            return;
        }

        if (this.form.invalid) {
            this.form.markAllAsTouched();

            Swal.fire({
                icon: 'warning',
                title: 'Formulario incompleto',
                text: 'Por favor completa todos los campos obligatorios.',
                confirmButtonColor: '#2563eb',
            });

            return;
        }

        this.enviando = true;

        Swal.fire({
            title: 'Enviando postulación',
            text: 'Por favor espera un momento...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const fd = new FormData();

        Object.keys(this.form.getRawValue()).forEach(key => {
            const value = this.form.getRawValue()[key];

            if (value !== null && value !== undefined) {
                fd.append(key, value);
            }
        });

        this.prospectoService.create(fd).subscribe({
            next: () => {
                this.enviando = false;
                this.enviado = true;
                this.mensajeExito =
                    'Usted ha completado el formulario, cualquier novedad se le informará.';
                this.form.disable();

                Swal.fire({
                    icon: 'success',
                    title: 'Postulación enviada',
                    text: this.mensajeExito,
                    confirmButtonColor: '#2563eb'
                });
            },
            error: (err) => {
                this.enviando = false;
                this.enviado_again = true;
                this.mensajeError =
                    err?.error?.error ||
                    'Hubo un problema al enviar el formulario';

                Swal.fire({
                    icon: 'error',
                    title: 'No se pudo enviar',
                    text: this.mensajeError,
                    confirmButtonColor: '#dc2626'
                });
            }
        });
    }
    readonly maxCvSizeMb = 20;
    readonly maxCvSizeBytes = this.maxCvSizeMb * 1024 * 1024;
    isDraggingCv = false;
    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        this.handleCvFile(file);

        // Permite volver a seleccionar el mismo archivo si el usuario se equivoca
        input.value = '';
    }

    onCvDragEnter(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.canUseRecruitmentForm || this.enviando) return;

        this.isDraggingCv = true;
    }

    onCvDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.canUseRecruitmentForm || this.enviando) return;

        this.isDraggingCv = true;
    }

    onCvDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.isDraggingCv = false;
    }

    onCvDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.isDraggingCv = false;

        if (!this.canUseRecruitmentForm || this.enviando) return;

        const file = event.dataTransfer?.files?.[0];
        this.handleCvFile(file);
    }

    private handleCvFile(file?: File): void {
        if (!file) return;

        const isPdf =
            file.type === 'application/pdf' ||
            file.name.toLowerCase().endsWith('.pdf');

        if (!isPdf) {
            Swal.fire({
                icon: 'warning',
                title: 'Archivo no permitido',
                text: 'Solo se permiten archivos PDF.',
                confirmButtonColor: '#dc2626',
            });
            return;
        }

        if (file.size > this.maxCvSizeBytes) {
            Swal.fire({
                icon: 'warning',
                title: 'Archivo demasiado grande',
                text: `El archivo supera el tamaño máximo permitido de ${this.maxCvSizeMb} MB.`,
                confirmButtonColor: '#dc2626',
            });
            return;
        }

        this.cvNombre = file.name;

        this.form.patchValue({ cv: file });
        this.form.get('cv')?.markAsDirty();
        this.form.get('cv')?.markAsTouched();
        this.form.get('cv')?.updateValueAndValidity();
    }
    descargarCV() {
        const file = this.form.get('cv')?.value;

        if (!file) return;

        const url = URL.createObjectURL(file);

        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();

        URL.revokeObjectURL(url);
    }
    private calcularEdadAutomaticamente(): void {
        this.form.get('birth_date')?.valueChanges.subscribe((fecha: string) => {
            if (!fecha) {
                this.form.get('age')?.setValue(null);
                return;
            }
            const edad = this.calcularEdadDesdeFecha(fecha);
            this.form.get('age')?.setValue(edad);
        });
    }

    private calcularEdadDesdeFecha(fechaNacimiento: string): number {
        const hoy = new Date();
        const nacimiento = new Date(fechaNacimiento);
        let edad = hoy.getFullYear() - nacimiento.getFullYear();
        const mes = hoy.getMonth() - nacimiento.getMonth();
        if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
            edad--;
        }
        return edad;
    }

    private obtenerUbicaciones() {
        this.ubicacionService.obtenerUbicaciones().subscribe({
            next: (resp) => {
                this.provincias = Object.values(resp)
                    .map((p: any) => p.provincia)
                    .filter((provincia: string) => provincia !== undefined);


                this.cantones = Object.keys(resp).map((key) => {
                    const cantonesPorProvincia = resp[key].cantones;
                    // Extraemos los nombres de los cantones para cada provincia
                    return {
                        provincia: resp[key].provincia,
                        cantones: Object.values(cantonesPorProvincia).map((canton: any) => ({
                            nombre: canton.canton,
                            parroquias: canton.parroquias ? Object.values(canton.parroquias) : []
                        }))
                    };
                });
            },
            error: (error) => {
                console.error(error)
            }
        })
    }
    onPaisTipoChange(event: any) {
        const valor = event.target.value
        this.esEcuador = valor === 'ECUADOR';

        if (!this.esEcuador && valor != null) {
            ['province', 'canton', 'village'].forEach(c => {
                this.form.get(c)?.setValidators([Validators.required]);
                this.form.get(c)?.updateValueAndValidity();
                this.form.patchValue({
                    province: null,
                    canton: null,
                    village: null
                })
            });
        } else {
            ['province', 'canton', 'village'].forEach(c => {
                this.form.get(c)?.setValidators([Validators.required]);
                this.form.get(c)?.updateValueAndValidity();
            });
        }
    }

    onProvinciaChange(event: any) {
        const selectedProvince = event.target.value;
        this.selectedCantones = this.cantones.find(c => c.provincia === selectedProvince)?.cantones || [];
    }

    onCantonChange(event: any) {
        const selectedCanton = event.target.value;
        this.selectedParroquias = this.selectedCantones.find(c => c.nombre === selectedCanton)?.parroquias || []
    }

    soloNumeros(event: Event): void {
        const input = event.target as HTMLInputElement;
        input.value = input.value.replace(/[^0-9]/g, '');
        this.form.get('id_card')?.setValue(input.value, { emitEvent: false });
    }

    onProvinceChange(event: any) {
        const selectedProvince = event.target.value;
        this.selectedCantones = this.cantones.find(c => c.provincia === selectedProvince)?.cantones || [];
    }

    getCantonesPorProvincia(provincia: string) {
        return this.cantones.find(c => c.provincia === provincia)?.cantones || [];
    }

    private setMaxBirthDate(): void {
        const today = new Date();
        today.setFullYear(today.getFullYear() - 18);

        this.maxBirthDate = today.toISOString().split('T')[0];
    }
    limitInteger(event: Event, control: string, maxDigits: number): void {
        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\D/g, '');
        value = value.slice(0, maxDigits);
        input.value = value;
        this.form.get(control)?.setValue(value, { emitEvent: false });
    }
    isInvalid(path: string, group?: AbstractControl): boolean {
        const control = group
            ? group.get(path)
            : this.form.get(path);

        return !!(control && control.invalid && (control.touched || control.dirty));
    }

    currentStep = 0;

    steps = [
        {
            label: 'Identificación',
            fields: ['id_card', 'name', 'father_surname', 'mother_surname']
        },
        {
            label: 'Información personal',
            fields: ['birth_date', 'age', 'civil_status']
        },
        {
            label: 'Lugar de nacimiento',
            fields: ['country', 'province', 'canton', 'village']
        },
        {
            label: 'Contacto y domicilio',
            fields: ['email', 'phone', 'city']
        },
        {
            label: 'Perfil profesional',
            fields: ['vocation', 'work_experience', 'cv']
        },
        {
            label: 'Revisión',
            fields: []
        }
    ];

    completedSteps: number[] = [];

    get progressPercentage(): number {
        return ((this.currentStep + 1) / this.steps.length) * 100;
    }

    isStepCompleted(index: number): boolean {
        return this.completedSteps.includes(index);
    }

    isStepAccessible(index: number): boolean {
        if (!this.canUseRecruitmentForm) return false;
        if (index === this.currentStep) return true;
        if (this.isStepCompleted(index)) return true;
        if (index < this.currentStep) return true;

        // Permitir ir solo al siguiente si el actual ya está completo
        if (index === this.currentStep + 1 && this.validateStep(this.currentStep, false)) {
            return true;
        }

        return false;
    }

    validateStep(stepIndex: number, markTouched: boolean = true): boolean {
        const fields = this.steps[stepIndex]?.fields ?? [];
        let valid = true;

        for (const field of fields) {
            const control = this.form.get(field);

            if (!control) continue;

            if (markTouched) {
                control.markAsTouched();
            }

            control.updateValueAndValidity();

            if (control.invalid) {
                valid = false;
            }
        }

        if (valid) {
            if (!this.completedSteps.includes(stepIndex)) {
                this.completedSteps.push(stepIndex);
            }
        } else {
            this.completedSteps = this.completedSteps.filter(step => step !== stepIndex);
        }

        return valid;
    }

    nextStep(): void {
        if (!this.canUseRecruitmentForm) {
            Swal.fire({
                icon: 'warning',
                title: 'Autorización requerida',
                text: 'Debe aceptar el tratamiento de datos personales antes de completar el formulario.',
                confirmButtonColor: '#e10600',
            });

            return;
        }
        const isValid = this.validateStep(this.currentStep, true);

        if (!isValid) return;

        if (this.currentStep < this.steps.length - 1) {
            this.currentStep++;
        }
    }

    prevStep(): void {
        if (this.currentStep > 0) {
            this.currentStep--;
        }
    }

    goToStep(index: number): void {
        if (!this.canUseRecruitmentForm) return;
        if (index === this.currentStep) return;

        // Siempre puede regresar a pasos previos
        if (index < this.currentStep) {
            this.currentStep = index;
            return;
        }

        // Si quiere ir hacia adelante, validar el paso actual primero
        const currentValid = this.validateStep(this.currentStep, true);

        if (!currentValid) return;

        // Solo permitir ir a pasos ya completados o al siguiente
        if (index === this.currentStep + 1 || this.isStepCompleted(index)) {
            this.currentStep = index;
        }
    }
    transformInput(
        event: any,
        controlName: string,
        permitirEspacios?: boolean,
        upperCase?: boolean
    ) {
        let value = event.target.value ?? '';

        if (!permitirEspacios) {
            value = value.replace(/\s/g, '');
        } else {
            value = value.replace(/\s+/g, ' ').trimStart();
        }
        if (upperCase) {
            value = value.toUpperCase();
        } else {
            value = value.toLowerCase();
        }

        const control = this.form.get(controlName);
        control?.setValue(value, { emitEvent: false });
    }
    get privacyGateRequired(): boolean {
        return !!(
            this.privacyPolicy?.external_id &&
            this.privacyPolicy?.requires_acceptance !== false
        );
    }

    get canUseRecruitmentForm(): boolean {
        if (!this.privacyGateRequired) return true;

        return (
            this.privacyAccepted &&
            this.privacyContactAccepted &&
            this.privacyTruthAccepted
        );
    }

    get companyName(): string {
        return this.privacyPolicy?.company_name || 'NOVAX';
    }

    onPrivacyCheckChange(): void {
        this.form.patchValue(
            {
                privacy_policy_id: this.privacyPolicy?.external_id || null,
                privacy_policy_external_id: this.privacyPolicy?.external_id || null,
                data_treatment_accepted: this.privacyAccepted,
                contact_authorization_accepted: this.privacyContactAccepted,
                truth_declaration_accepted: this.privacyTruthAccepted,
            },
            { emitEvent: false },
        );

        this.applyPrivacyGate();
    }

    private applyPrivacyGate(): void {
        if (!this.form) return;

        if (this.canUseRecruitmentForm) {
            this.form.enable({ emitEvent: false });
        } else {
            this.form.disable({ emitEvent: false });
        }
    }

    openPrivacyPolicyDocument(): void {
        if (!this.privacyPolicy?.external_id || this.privacyPdfLoading) return;

        this.revokePrivacyPdfObjectUrl();

        this.privacyModalOpen = true;
        this.privacyPdfLoading = true;
        this.privacyPdfSafeUrl = null;
        document.body.classList.add('sp-policy-modal-open');

        this.policyService.view(this.privacyPolicy.external_id).subscribe({
            next: (blob: Blob) => {
                const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                this.privacyPdfObjectUrl = URL.createObjectURL(pdfBlob);

                const viewerUrl = `${this.privacyPdfObjectUrl}#toolbar=0&navpanes=0&scrollbar=1`;
                this.privacyPdfSafeUrl =
                    this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
            },
            error: (error) => {
                console.error(error);
                this.privacyPdfLoading = false;
                this.closePrivacyPolicyDocument();

                Swal.fire({
                    icon: 'error',
                    title: 'No se pudo cargar el documento',
                    text:
                        error?.error?.error ||
                        error?.error?.message ||
                        'Intente nuevamente en unos minutos.',
                    confirmButtonColor: '#e10600',
                });
            },
        });
    }

    closePrivacyPolicyDocument(): void {
        this.privacyModalOpen = false;
        this.privacyPdfLoading = false;
        this.privacyPdfSafeUrl = null;

        this.revokePrivacyPdfObjectUrl();

        document.body.classList.remove('sp-policy-modal-open');
    }

    onPrivacyPdfLoaded(): void {
        this.privacyPdfLoading = false;
    }

    private revokePrivacyPdfObjectUrl(): void {
        if (this.privacyPdfObjectUrl) {
            URL.revokeObjectURL(this.privacyPdfObjectUrl);
            this.privacyPdfObjectUrl = null;
        }
    }
}

